import { useQuery } from '@tanstack/react-query';
import type { SavingsReconciliationStatus } from '@lizrd/contracts';
import { Card } from '../ui/card';
import { SkeletonText } from '../ui/skeleton';
import {
  recoverableCount,
  recoverableSaving,
  realizedSince,
  validatedRecs,
  monthStart,
} from '../../lib/savings';
import { useOrgDomainRecs } from './useOrgDomainRecs';
import { getSavingsReconciliation } from '../../lib/api';
import { usd } from '../../lib/graph-visuals';

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** Dot colour for how the validated savings reconcile against the actual bill. */
const RECONCILE_TONE: Record<SavingsReconciliationStatus, string> = {
  corroborated: 'bg-success',
  partially_corroborated: 'bg-warning',
  offset_by_growth: 'bg-warning',
  insufficient_data: 'bg-muted',
};

/**
 * The org-wide savings headline — one box, two figures over every domain the user
 * can access: what's still RECOVERABLE per month (the open opportunity) and what
 * teams have REALIZED this month (validated). Both come from the recommendations —
 * the same source the leaderboard and "recoverable by category" use — so the numbers
 * always reconcile. These are estimate-based (from each recommendation's projected
 * saving), so we say so.
 */
export function SavingsBand() {
  const org = useOrgDomainRecs();
  // Ground the validated savings against the actual bill (TD-011/TD-070). Only
  // shown once there are two settled billing months to compare — otherwise the
  // estimate stands on its own.
  const reconcile = useQuery({
    queryKey: ['savings-reconciliation'],
    queryFn: getSavingsReconciliation,
    staleTime: 60 * 60_000,
  });

  if (!org.loaded) {
    return (
      <Card className="p-5">
        <SkeletonText lines={2} lineClassName="h-4" />
      </Card>
    );
  }

  const allRecs = org.envs.flatMap((e) => e.recs);
  const recoverable = recoverableSaving(allRecs);
  const recoverableN = recoverableCount(allRecs);
  const realizedThisMonth = realizedSince(allRecs, monthStart(new Date()));
  const realizedN = validatedRecs(allRecs).filter(
    (r) => new Date(r.validatedAt!) >= monthStart(new Date()),
  ).length;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          Savings across your domains
        </h2>
        <span
          className="inline-flex items-center gap-1 rounded-full border border-hairline px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted"
          title="Savings are estimated from each recommendation's projected monthly impact."
        >
          <span className="h-1.5 w-1.5 rounded-full bg-muted" aria-hidden />
          Estimated
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="font-display text-3xl font-extrabold text-iris tnum">
            {usd(recoverable)}
          </div>
          <div className="mt-0.5 text-xs font-medium text-muted">
            Recoverable / month
            <span className="text-muted">
              {' '}
              · {plural(recoverableN, 'open recommendation')} · ~{usd(recoverable * 12)}/yr
            </span>
          </div>
        </div>
        <div>
          <div className="font-display text-3xl font-extrabold text-success tnum">
            {usd(realizedThisMonth)}
          </div>
          <div className="mt-0.5 text-xs font-medium text-muted">
            Realized this month
            <span className="text-muted"> · {plural(realizedN, 'validated saving')}</span>
          </div>
        </div>
      </div>
      {reconcile.data && reconcile.data.status !== 'insufficient_data' && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-hairline bg-surface-2 px-3 py-2">
          <span
            className={`mt-1 h-1.5 w-1.5 flex-none rounded-full ${RECONCILE_TONE[reconcile.data.status]}`}
            aria-hidden
          />
          <p className="text-xs leading-relaxed text-haze">
            <span className="font-semibold text-bone">Against your actual bill: </span>
            {reconcile.data.note}
          </p>
        </div>
      )}
    </Card>
  );
}
